import { useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuthContext } from "v4/contexts/AuthContext";
import { resolveRouteComponent } from "v4/features/front/navigation/utils/navigation.mapper";
import { patternToRegex, getPatternMetadata } from "v4/features/front/navigation/utils/navigation.utils";

export default function useCurrentRoute(navigationContext) {
    const { getRouteConfigByPathname, getRouteByNativeIdentifier } = navigationContext;
    const { push, location: { pathname } } = useHistory();
    const { user } = useAuthContext();

    useEffect(() => {
        if (!user || pathname !== '/') return;
        const mainProspectsRoute = getRouteByNativeIdentifier('main_prospects');
        if (mainProspectsRoute) push(`/${mainProspectsRoute.url}`);
    }, [pathname, getRouteByNativeIdentifier, push, user]);

    return useMemo(() => {
        const result = getRouteConfigByPathname(pathname);
        if (!result?.item) return { route: null, routeConfig: null, isDetail: false, isCustomTab: false, routeType: null, params: {} };

        const { item: route, isDetail } = result;
        const routeConfig = resolveRouteComponent(route, isDetail) || {};
        const params = {};
        const cleanPathname = pathname.replace(/^\/+|\/+$/g, '');

        // 1. Préparer les patterns (Base + Additional Paths)
        const patterns = [{ path: route.url, isBase: true, ...getPatternMetadata(route.url) }];

        if (routeConfig.additionalPaths && route.url) {
            const baseUrl = route.url.replace(/\/+$/, '');
            routeConfig.additionalPaths.forEach(ap => {
                const subPath = typeof ap === 'object' ? ap.path : ap;
                if (!subPath) return;

                const fullPath = `${baseUrl}/${subPath.replace(/^\/+/, '')}`;
                patterns.push({ 
                    path: fullPath, 
                    type: ap.routeType, 
                    isBase: false,
                    ...getPatternMetadata(fullPath)
                });
            });
        }

        // 2. Trouver le meilleur match et extraire les paramètres
        const sorted = patterns.sort((a, b) => b.score - a.score || a.placeholderCount - b.placeholderCount);
        let matched = null;

        sorted.some(p => {
            const regex = patternToRegex(p.path, true); // On autorise les sous-chemins
            const match = cleanPathname.match(regex);
            
            if (match) {
                matched = p;
                const paramNames = (p.path.match(/\{[a-zA-Z0-9_-]+\}/g) || []).map(s => s.slice(1, -1));
                paramNames.forEach((name, i) => { if (match[i + 1]) params[name] = match[i + 1]; });
                return true; 
            }
            return false;
        });

        // 3. Déterminer le type de route (LISTING vs SHOW)
        let routeType = (matched?.isBase && isDetail && !routeConfig.additionalPaths?.length) || (matched && !matched.isBase) 
            ? (matched.type || 'SHOW') 
            : 'LISTING';

        return {
            route,
            routeConfig: routeConfig?.component ? routeConfig : null,
            isDetail,
            isCustomTab: !route.native_identifier,
            routeType,
            params,
            groupId: (isDetail && routeConfig.groupCode) ? params[routeConfig.groupCode] : null,
        };
    }, [pathname, getRouteConfigByPathname]);
}
